import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { setCacheVersion } from '@/services/utils/websocket';

const CACHEABLE_KEY = 'http-cacheable-resources';
const CACHE_VERSION_KEY = 'http-cache-version';

function getCacheableResources(): Map<string, number> {
  try {
    const stored = localStorage.getItem(CACHEABLE_KEY) || '[]';
    return new Map(JSON.parse(stored));
  } catch {
    return new Map();
  }
}

function pruneExpiredResources(resources: Map<string, number>): Map<string, number> {
  const now = Date.now();
  for (const [resource, expiry] of resources.entries()) {
    if (now > expiry) resources.delete(resource);
  }
  return resources;
}

function addCacheableResource(resource: string, maxAge: number): void {
  const resources = pruneExpiredResources(getCacheableResources());
  const expiry = maxAge > 0 ? Date.now() + maxAge * 1000 : maxAge;
  resources.set(resource, expiry);
  localStorage.setItem(CACHEABLE_KEY, JSON.stringify(Array.from(resources.entries())));
}

function isCacheableResource(resource: string): boolean {
  return pruneExpiredResources(getCacheableResources()).has(resource);
}

function interceptRequest(config: AxiosRequestConfig): AxiosRequestConfig {
  const method = (config.method ?? 'get').toUpperCase();

  if (method === 'GET') {
    if (config.url && isCacheableResource(config.url)) {
      let cacheVersion = localStorage.getItem(CACHE_VERSION_KEY);
      if (!cacheVersion) cacheVersion = setCacheVersion(`${Date.now()}`);

      config.params = { ...config.params, cacheVersion };
    } else {
      config.headers = config.headers ?? {};
      config.headers['Cache-Control'] = 'no-cache';
      config.headers['Pragma'] = 'no-cache';
      config.headers['Expires'] = '0';
    }
  } else {
    setCacheVersion(`${Date.now()}`);
  }

  return config;
}

function interceptResponse(response: AxiosResponse): AxiosResponse {
  const cacheControl = response.headers['cache-control'];
  const url = response.config?.url;

  if (
    url &&
    cacheControl &&
    !cacheControl.includes('no-cache') &&
    !cacheControl.includes('no-store')
  ) {
    const match = cacheControl.match(/max-age=(\d+)/);
    const maxAge = match ? parseInt(match[1], 10) : -1;
    addCacheableResource(url, maxAge);
  }

  return response;
}

export function registerCacheControl(api: AxiosInstance): void {
  api.interceptors.request.use(interceptRequest);
  api.interceptors.response.use(interceptResponse);
}